import { format } from 'date-fns';

import { MwdDeeltraject, MwdWerklijn } from '@/apps/mwd-v2/types/Domain';
import ChevronLeft from '@/assets/icons/ChevronLeft';
import ChevronRight from '@/assets/icons/ChevronRight';
import Close from '@/assets/icons/Close';
import CurrentLocationXs from '@/assets/icons/CurrentLocationXs';
import { useDetailPanelHeader } from '@/components/detailPanel/header/useDetailPanelHeader';
import { DetailInzetModel } from '@/queries/queryModels/detailInzetModel';
import { formatTime, getDutchDayAbbreviation, isSignificantlyDelayedOrAhead } from '@/utils/dateUtils';

import styles from '../DetailPanel.module.css';

interface DetailPanelHeaderProps {
  werklijn?: MwdWerklijn;
  selectDeeltraject: (deeltraject: MwdDeeltraject) => void;
  hideDetailPanel: () => void;
  mwdDeeltraject: MwdDeeltraject | undefined;
  detailPanelData?: DetailInzetModel | null;
}

export default function DetailPanelHeader({
  werklijn,
  selectDeeltraject,
  mwdDeeltraject,
  detailPanelData,
  hideDetailPanel,
}: Readonly<DetailPanelHeaderProps>) {
  const { handleNextInzetClick, handlePrevInzetClick } = useDetailPanelHeader({
    werklijn,
    mwdDeeltraject,
    selectDeeltraject,
  });

  return (
    <div className={styles.detailPanelHeader}>
      <div className={styles.detailPanelTitle}>
        <h3>
          Inzet {mwdDeeltraject?.treinnummer}
          {mwdDeeltraject?.inzetPositie ? `-${mwdDeeltraject.inzetPositie}` : ''}
        </h3>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '22px',
          }}
        >
          <a
            data-testid='nav-icon-left'
            className={styles.navIcon}
            onClick={handlePrevInzetClick}
            style={{ textDecoration: 'none' }}
          >
            <ChevronLeft width={16} height={16} />
          </a>
          <a
            data-testid='nav-icon-right'
            className={styles.navIcon}
            onClick={handleNextInzetClick}
            style={{ textDecoration: 'none' }}
          >
            <ChevronRight width={16} height={16} />
          </a>

          <button data-testid='close-detailPanel' className={styles.closeButton} onClick={hideDetailPanel}>
            <Close fill='var(--tractie-color-content-subtle)' width={25} height={25} />
          </button>
        </div>
      </div>
      <div className={styles.detailPanelHeaderInfo}>
        <div data-testid={`${detailPanelData?.inzetId}-omloop`}>Omloop {mwdDeeltraject?.omloop}</div>
      </div>
      <div className={styles.detailPanelHeaderDetails}>
        <ul>
          <li className={styles.listItemRow}>
            {detailPanelData?.route?.laatstGemetenLocatie?.dienstregelpunt && (
              <>
                <span>Laatste locatie</span>
                <span data-testid={`${detailPanelData?.inzetId}-locatie`}>
                  {detailPanelData?.route?.laatstGemetenLocatie?.dienstregelpunt ?? ' '}
                  {detailPanelData?.route?.laatstGemetenLocatie?.tijd ? (
                    <>
                      <CurrentLocationXs fill='var(--tractie-color-content-emphasis)' width={18} height={18} />
                      {`(${format(new Date(detailPanelData.route.laatstGemetenLocatie.tijd), 'HH:mm')})`}
                    </>
                  ) : (
                    ''
                  )}
                </span>
              </>
            )}
          </li>
          <li className={styles.listItemRow}>
            <span>Van</span>
            <span
              style={{
                display: 'flex',
                gap: '6px',
                flexDirection: isSignificantlyDelayedOrAhead(
                  mwdDeeltraject?.vertrekOorsponkelijkeTijd as Date,
                  mwdDeeltraject?.vertrekUitvoeringsTijd as Date,
                )
                  ? 'column'
                  : 'row',
              }}
            >
              <span
                data-testid={`${detailPanelData?.inzetId}-van-tijd`}
                className={
                  isSignificantlyDelayedOrAhead(
                    mwdDeeltraject?.vertrekOorsponkelijkeTijd as Date,
                    mwdDeeltraject?.vertrekUitvoeringsTijd as Date,
                  )
                    ? styles.strikeThroughStyle
                    : ''
                }
              >
                {`${getDutchDayAbbreviation(mwdDeeltraject?.vertrekOorsponkelijkeTijd)} ${formatTime(mwdDeeltraject?.vertrekOorsponkelijkeTijd)}`}
              </span>{' '}
              <span>
                {isSignificantlyDelayedOrAhead(
                  mwdDeeltraject?.vertrekOorsponkelijkeTijd as Date,
                  mwdDeeltraject?.vertrekUitvoeringsTijd as Date,
                ) && (
                  <span
                    style={{
                      paddingRight: '6px',
                      color:
                        (mwdDeeltraject?.vertrekOorsponkelijkeTijd ?? 0) < (mwdDeeltraject?.vertrekUitvoeringsTijd ?? 0)
                          ? 'var(--tractie-color-content-error)'
                          : 'var(--tractie-color-background-success-default)',
                    }}
                  >
                    <strong>
                      {' '}
                      {`${getDutchDayAbbreviation(mwdDeeltraject?.vertrekUitvoeringsTijd ?? new Date(0))} ${formatTime(mwdDeeltraject?.vertrekUitvoeringsTijd ?? new Date(0))}`}
                    </strong>
                  </span>
                )}
                <span
                  style={{
                    color: mwdDeeltraject?.plaatsConflictVan ? 'var(--tractie-color-content-error' : '',
                    fontWeight: mwdDeeltraject?.plaatsConflictVan ? '600' : '400',
                  }}
                >
                  {mwdDeeltraject?.dienstregelpuntVan}
                </span>
              </span>
            </span>
          </li>
          <li className={styles.listItemRow}>
            <span>Naar</span>
            <span
              style={{
                display: 'flex',
                gap: '6px',
                flexDirection: isSignificantlyDelayedOrAhead(
                  mwdDeeltraject?.aankomstOorsponkelijkeTijd as Date,
                  mwdDeeltraject?.aankomstUitvoeringsTijd as Date,
                )
                  ? 'column'
                  : 'row',
              }}
            >
              <span
                data-testid={`${detailPanelData?.inzetId}-naar-tijd`}
                className={
                  isSignificantlyDelayedOrAhead(
                    mwdDeeltraject?.aankomstOorsponkelijkeTijd as Date,
                    mwdDeeltraject?.aankomstUitvoeringsTijd as Date,
                  )
                    ? styles.strikeThroughStyle
                    : ''
                }
              >
                {`${getDutchDayAbbreviation(mwdDeeltraject?.aankomstOorsponkelijkeTijd)} ${formatTime(mwdDeeltraject?.aankomstOorsponkelijkeTijd)}`}
              </span>{' '}
              <span>
                {isSignificantlyDelayedOrAhead(
                  mwdDeeltraject?.aankomstOorsponkelijkeTijd as Date,
                  mwdDeeltraject?.aankomstUitvoeringsTijd as Date,
                ) && (
                  <span
                    style={{
                      paddingRight: '6px',
                      color:
                        (mwdDeeltraject?.aankomstOorsponkelijkeTijd ?? 0) <
                        (mwdDeeltraject?.aankomstUitvoeringsTijd ?? 0)
                          ? 'var(--tractie-color-content-error)'
                          : 'var(--tractie-color-background-success-default)',
                    }}
                  >
                    <strong>
                      {' '}
                      {`${getDutchDayAbbreviation(mwdDeeltraject?.aankomstUitvoeringsTijd ?? new Date(0))} ${formatTime(mwdDeeltraject?.aankomstUitvoeringsTijd ?? new Date(0))}`}
                    </strong>
                  </span>
                )}
                <span
                  style={{
                    color: mwdDeeltraject?.plaatsConflictNaar ? 'var(--tractie-color-content-error' : '',
                    fontWeight: mwdDeeltraject?.plaatsConflictNaar ? '600' : '400',
                  }}
                >
                  {mwdDeeltraject?.dienstregelpuntNaar}
                </span>
              </span>
            </span>
          </li>
          {mwdDeeltraject?.bijzonderheidscode && (
            <li className={styles.listItemRow}>
              <span>Bijzonderheid</span>
              <span>{mwdDeeltraject?.bijzonderheidscode}</span>
            </li>
          )}
          {mwdDeeltraject?.toelichting && (
            <li className={styles.listItemRow}>
              <span>Notitie</span>
              <span>{mwdDeeltraject?.toelichting}</span>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}
